import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ABTest } from '@freelancer/abtest';
import { Auth } from '@freelancer/auth';
import {
  BackendResponse,
  CartItem,
  Datastore,
  MilestoneDraft,
  toNumber,
} from '@freelancer/datastore';
import { Currency } from '@freelancer/datastore/currencies';
import { ModalRef } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor } from '@freelancer/ui/button';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { InputType } from '@freelancer/ui/input';
import { Margin } from '@freelancer/ui/margin';
import { FontWeight } from '@freelancer/ui/text';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import { maxLength, pattern, required } from '@freelancer/ui/validators';
import {
  CartStatusApi,
  ContextTypeApi,
  DestinationApi,
  ItemStatusApi,
} from 'api-typings/payments/payments';
import * as Rx from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

@Component({
  template: `
    <fl-bit class="Header">
      <fl-heading
        [size]="HeadingSize.XSMALL"
        [headingType]="HeadingType.H4"
        i18n="Create milestone modal header"
      >
        Create Milestone Payment
      </fl-heading>
    </fl-bit>
    <fl-bit class="MainBody">
      <fl-text
        [flMarginBottom]="Margin.SMALL"
        i18n="Create milestone modal body"
      >
        You can release this payment once you are 100% satisfied with the work
        provided.
      </fl-text>
    </fl-bit>
    <fl-grid>
      <fl-col [col]="12" [colTablet]="4" [flMarginBottom]="Margin.XSMALL">
        <fl-label
          [weight]="FontWeight.BOLD"
          i18n="Milestone amount input label"
        >
          Amount ({{ currencyDetails.code }})
        </fl-label>
        <fl-input
          [control]="formGroup.controls.amount"
          [type]="InputType.NUMBER"
          [beforeLabel]="currencyDetails.sign"
          [placeholder]="'25'"
        ></fl-input>
      </fl-col>
      <fl-col [col]="12" [colTablet]="8" [flMarginBottom]="Margin.XSMALL">
        <fl-label [weight]="FontWeight.BOLD" i18n="Description input label">
          Description
        </fl-label>
        <fl-input
          placeholder="e.g. Milestone for the design"
          i18n-placeholder="Example description for milestone description input"
          [flMarginBottom]="Margin.SMALL"
          [control]="formGroup.controls.description"
          [maxLength]="200"
        ></fl-input>
      </fl-col>
    </fl-grid>
    <ng-container *ngIf="(inTest$ | async); else NoCart">
      <fl-banner-alert
        *ngIf="(createMilestonePromise | async)?.status === 'error'"
        [type]="BannerAlertType.ERROR"
        [closeable]="false"
        [flMarginBottom]="Margin.XSMALL"
        i18n="Create milestone modal error alert"
      >
        The milestone creation request failed. Please try again or contact
        support@freelancer.com with request_id:
        {{ (createMilestonePromise | async)?.requestId }}
      </fl-banner-alert>
    </ng-container>
    <ng-template #NoCart>
      <fl-banner-alert
        *ngIf="showError"
        [type]="BannerAlertType.ERROR"
        [closeable]="false"
        [flMarginBottom]="Margin.XSMALL"
        i18n="Create milestone modal error alert"
      >
        There was a problem creating the milestone. Please try again or
        <fl-link [link]="'/support/employer'">contact support</fl-link>.
      </fl-banner-alert>
    </ng-template>
    <fl-bit class="ActionContainer">
      <fl-button
        flTrackingLabel="milestoneModalCancelButton"
        [flMarginRight]="Margin.XXXSMALL"
        [color]="ButtonColor.DEFAULT"
        (click)="cancel()"
        i18n="Create milestone modal cancel button"
      >
        Cancel
      </fl-button>
      <ng-container>
        <fl-button
          *ngIf="(inTest$ | async)"
          flTrackingLabel="milestoneModalCreateButton"
          [busy]="
            createMilestonePromise && (createMilestonePromise | async) === null
          "
          [color]="ButtonColor.SECONDARY"
          [disabled]="!formGroup.valid"
          (click)="createMilestoneCart()"
          i18n="Create milestone modal create button"
        >
          Create Milestone
        </fl-button>
        <ng-container *ngIf="!(inTest$ | async)">
          <fl-button
            flTrackingLabel="milestoneModalCreateButton"
            [busy]="isBusy"
            [color]="ButtonColor.SECONDARY"
            [disabled]="!formGroup.valid"
            (click)="createMilestone()"
            i18n="Create milestone modal create button"
          >
            Create Milestone
          </fl-button>
        </ng-container>
      </ng-container>
    </fl-bit>
  `,
  styleUrls: ['./milestone-create-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MilestoneCreateModalComponent implements OnInit {
  Margin = Margin;
  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  FontWeight = FontWeight;
  HeadingSize = HeadingSize;
  HeadingType = HeadingType;
  InputType = InputType;

  formGroup: FormGroup;
  cartId: number;

  isBusy = false;
  showError = false;
  createMilestonePromise: Promise<BackendResponse>;
  inTest$: Rx.Observable<boolean>;

  @Input() projectId: number;
  @Input() bidId: number;
  @Input() currencyDetails: Currency;
  @Input() bidderId: number;
  @Input() username: string;

  constructor(
    private modalRef: ModalRef<MilestoneCreateModalComponent>,
    private datastore: Datastore,
    private router: Router,
    private auth: Auth,
    private abtest: ABTest,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(UI_CONFIG) private uiConfig: UiConfig,
  ) {}

  ngOnInit() {
    // set up input formgroup
    this.formGroup = new FormGroup({
      amount: new FormControl('', [
        required('Amount field is required'),
        pattern(/^[1-9][0-9]*$/, 'Invalid amount specified'),
      ]),
      description: new FormControl('', [
        required('Description is required'),
        maxLength(200, 'Please create a shorter description.'),
      ]),
    });

    if (this.abtest.isWhitelistUser()) {
      this.inTest$ = Rx.of(true);
    } else if (this.uiConfig.theme === 'arrow') {
      this.inTest$ = Rx.of(false);
    } else {
      this.inTest$ = Rx.combineLatest(
        this.auth.getUserId(),
        this.datastore
          .collection('projectViewProjects', query =>
            query.where('id', '==', this.projectId).limit(1),
          )
          .valueChanges(),
      ).pipe(
        switchMap(([authId, projects]) => {
          if (
            toNumber(authId) > 33337777 &&
            projects[0].language.includes('en') &&
            !projects[0].local
          ) {
            return this.abtest
              .getUserExperimentVariation('T65827-employer-pvp-webapp-v2')
              .pipe(map(variation => variation === 'test'));
          }

          return Rx.of(false);
        }),
      );
    }
  }

  // Create milestone without cart
  createMilestone() {
    this.isBusy = true;

    this.datastore
      .createObject('milestones', {
        id: Date.now(),
        projectId: this.projectId,
        bidderId: this.bidderId,
        amount: toNumber(this.formGroup.controls.amount.value),
        description: this.formGroup.controls.description.value,
        currency: this.currencyDetails,
        timeCreated: Date.now(),
      })
      .then(res => {
        if (res.status === 'success') {
          this.modalRef.close(true);
        } else if (res.errorCode === 'PAYMENT_REQUIRED') {
          this.modalRef.close(true);
          // if it fails due to insufficient funds, redirect to deposit page
          // refAction = create_milestone handles the dataToken for us
          const queryParams = {
            amount: toNumber(this.formGroup.controls.amount.value),
            bidderId: this.bidderId,
            currency: this.currencyDetails.id,
            descr: this.formGroup.controls.description.value,
            id: this.projectId, // id is projectId, gets transformed to `pid` later
            refAction: 'create_milestone',
            username: this.username,
            checkBalance: true,
          };
          this.router.navigate(['/deposit'], { queryParams });
        } else {
          // otherwise show the generic error
          this.showError = true;
          this.isBusy = false;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  /**
   * 1. create cart
   * 2. create milestone draft
   * 3. create cart item
   * 4. add project fee cart item if hourly
   * 5. If success, to payment page, all errors in all steps show and break promise
   *
   */
  createMilestoneCart() {
    this.createMilestonePromise = this.createCart()
      .then(cartResponse => {
        if (cartResponse.status !== 'success') {
          return cartResponse;
        }

        this.cartId = cartResponse.id;
        return this.createMilestoneDraft(this.formGroup);
      })
      .then(milestoneDraftResponse => {
        if (milestoneDraftResponse.status !== 'success') {
          return milestoneDraftResponse;
        }

        return this.createCartItem(this.formGroup, milestoneDraftResponse.id);
      })
      .then(cartItemResponse => {
        if (cartItemResponse.status !== 'success') {
          return cartItemResponse;
        }
        return Rx.combineLatest(
          this.datastore
            .document('projectViewProjects', this.projectId)
            .valueChanges(),
          this.datastore
            .collection('hourlyContracts', query =>
              query.where('projectId', '==', this.projectId),
            )
            .valueChanges(),
        )
          .pipe(take(1))
          .toPromise()
          .then(([project, hourlyContracts]) => {
            // if it's hourly, we need to attach a project fee cart item
            if (project.hourlyProjectInfo && hourlyContracts.length > 0) {
              return this.datastore
                .collection('hourlyProjectMilestoneFees', query =>
                  query
                    .where('projectId', '==', this.projectId)
                    .where(
                      'milestoneAmount',
                      '==',
                      toNumber(this.formGroup.controls.amount.value),
                    ),
                )
                .valueChanges()
                .pipe(
                  take(1),
                  map(res => res[0]),
                )
                .toPromise()
                .then(r => {
                  const { fee, currencyId } = r;
                  return this.datastore.createObject('cartItems', {
                    contextType: ContextTypeApi.PROJECT_FEE,
                    contextId: String(cartItemResponse.id),
                    description: 'Project fee',
                    currencyId,
                    amount: fee,
                    cartId: this.cartId,
                    status: ItemStatusApi.NOT_DONE,
                    id: Date.now(),
                    useBonus: false,
                  });
                });
            }
            return cartItemResponse;
          });
      })
      .then(cartItemResponse => {
        if (cartItemResponse.status !== 'success') {
          return cartItemResponse;
        }

        this.cancel();
        this.navigateToDepositPage();
        return cartItemResponse;
      });
  }

  private createCart() {
    return this.datastore.createObject('carts', {
      id: Date.now(),
      status: CartStatusApi.UNPAID,
      time_created: Date.now(),
      userId: Date.now(), // user id is determined later from header
      returnAction: {
        destination: DestinationApi.PROJECT_VIEW_PAGE,
        payload: `${this.projectId}`,
      },
      description: `Chat box create milestone for project ${this.projectId}`,
    });
  }

  private createMilestoneDraft(milestoneForm: FormGroup) {
    const milestoneDraft: MilestoneDraft = {
      amount: Number(milestoneForm.value.amount),
      bidderId: this.bidderId,
      bidId: this.bidId,
      currencyId: this.currencyDetails.id,
      description: milestoneForm.value.description,
      id: Date.now(), // dummy,
      milestoneRequestId: undefined,
      projectId: this.projectId,
      projectOwnerId: Date.now(), // dummy
      timeCreated: Date.now(),
      transactionId: undefined,
    };

    return this.datastore.createObject('milestoneDrafts', milestoneDraft);
  }

  private createCartItem(milestoneForm: FormGroup, milestoneDraftId: number) {
    const cartItem: CartItem = {
      contextType: ContextTypeApi.MILESTONE,
      contextId: String(milestoneDraftId),
      description: milestoneForm.value.description,
      currencyId: this.currencyDetails.id,
      amount: Number(milestoneForm.value.amount),
      cartId: this.cartId,
      status: ItemStatusApi.NOT_DONE,
      id: Date.now(),
      useBonus: true,
    };
    return this.datastore.createObject('cartItems', cartItem);
  }

  private navigateToDepositPage() {
    this.router.navigate(['deposit'], {
      queryParams: { cartId: this.cartId },
    });
  }

  cancel() {
    this.modalRef.close(false);
  }
}
